import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { playbackQueryOptions } from '@/lib/queries/playback'

interface PlaybackState {
	status: 'idle' | 'playing'
	playlistId?: string
	songId?: string
	songName?: string
	songArtist?: string
	songIndex?: number
	totalSongs?: number
	startedAt?: number
}

interface DownloadState {
	songId: string | null
	songName?: string
	songArtist?: string
	playlistId?: string
}

interface PlaybackContextValue {
	state: PlaybackState
	elapsed: number
	isPlaying: boolean
	download: DownloadState
}

const PlaybackContext = createContext<PlaybackContextValue>({
	state: { status: 'idle' },
	elapsed: 0,
	isPlaying: false,
	download: { songId: null },
})

export function PlaybackProvider({ children }: { children: ReactNode }) {
	const { data } = useQuery(playbackQueryOptions)
	const state: PlaybackState = data ?? { status: 'idle' }
	const isPlaying = state.status === 'playing'

	const queryClient = useQueryClient()
	const downloadData = queryClient.getQueryData<DownloadState>(['download'])
	const download: DownloadState = downloadData ?? { songId: null }

	// Re-render when download state changes
	useQuery<DownloadState>({ queryKey: ['download'], enabled: false })

	const [elapsed, setElapsed] = useState(0)

	useEffect(() => {
		if (!isPlaying || !state.startedAt) {
			setElapsed(0)
			return
		}

		setElapsed(Math.floor((Date.now() - state.startedAt) / 1000))

		const interval = setInterval(() => {
			setElapsed(Math.floor((Date.now() - state.startedAt!) / 1000))
		}, 1000)

		return () => clearInterval(interval)
	}, [isPlaying, state.startedAt])

	return (
		<PlaybackContext value={{ state, elapsed, isPlaying, download }}>
			{children}
		</PlaybackContext>
	)
}

export function usePlayback() {
	return useContext(PlaybackContext)
}
